import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  SsoIntegrationCreateInputObject,
  SsoIntegrationUpdateInputObject,
  SsoMappingField,
  useSsoIntegrationCreateMutation,
  useSsoIntegrationUpdateMutation,
} from 'graphql-common';
import useSsoIntegrationQueryHook from 'utils/fetch/hooks/useSsoIntegrationQueryHook';
import { Values } from '@lib/interfaces/form';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import toast from '@lib/components/Toast/Toast';
import { APP_URLS } from 'constants/urls';
import getPreparedDataFromValues from './getPreparedDataFromValues';
import getDefaultValuesFromQueryData from './getDefaultValuesFromQueryData';

export default function useSsoFormActions({
  isEdit,
  supportedMappingFields = [],
}: {
  isEdit?: boolean;
  supportedMappingFields?: SsoMappingField[];
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id = '' } = useParams();

  // Query (edit mode)
  const {
    data,
    loading: dataLoading,
    firstLoading,
  } = useSsoIntegrationQueryHook({ id });

  // Mutations
  const [createMutation, createMutationResult] =
    useSsoIntegrationCreateMutation();
  const [updateMutation, updateMutationResult] =
    useSsoIntegrationUpdateMutation();

  const onSubmitForm = (values: Values) => {
    const prevValues = getDefaultValuesFromQueryData(
      data,
      t,
      supportedMappingFields,
    );
    const prepared = getPreparedDataFromValues(
      values,
      isEdit,
      t,
      supportedMappingFields,
      prevValues,
    );

    if (isEdit) {
      updateMutation({
        variables: {
          id,
          attributes: prepared as SsoIntegrationUpdateInputObject,
        },
        onCompleted: () => {
          toast({ content: t('update-sso-connection-success') });
          navigate(
            APP_URLS.dashboard.sso.view.getDynamicPath({
              pathParts: { id },
            }),
          );
        },
        onError: (error) => showGraphQLErrors(error, t),
      });
    } else {
      createMutation({
        variables: {
          attributes: prepared as SsoIntegrationCreateInputObject,
        },
        onCompleted: (completedData) => {
          toast({ content: t('create-sso-connection-success') });
          const newId = completedData?.data?.id;
          if (newId) {
            navigate(
              APP_URLS.dashboard.sso.view.getDynamicPath({
                pathParts: { id: newId },
              }),
            );
          } else {
            navigate(APP_URLS.dashboard.sso.index.getPathWithQuery());
          }
        },
        onError: (error) => showGraphQLErrors(error, t),
      });
    }
  };

  const onCancel = () => {
    if (isEdit && id) {
      navigate(
        APP_URLS.dashboard.sso.view.getDynamicPath({
          pathParts: { id },
        }),
      );
    } else {
      navigate(APP_URLS.dashboard.sso.index.getPathWithQuery());
    }
  };

  const loading =
    dataLoading || createMutationResult.loading || updateMutationResult.loading;
  const graphQLErrors =
    createMutationResult?.error?.graphQLErrors ||
    updateMutationResult?.error?.graphQLErrors;

  return {
    data,
    firstLoading,
    graphQLErrors,
    loading,
    onCancel,
    onSubmitForm,
  };
}
